import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  ApiError,
  api,
  type CategoryNode,
  type Rule,
  type RuleField,
  type RuleOperator,
} from '../../lib/api';
import { money, parseAmountInput } from '../../lib/format';

/**
 * Regras de categorizacao.
 *
 * A regra sugere; ela nao grava sozinha. Na importacao a sugestao aparece na
 * conferencia antes de virar lancamento, e aqui a reaplicacao no que ja esta
 * lancado tem simulacao antes de valer. Categorizar em silencio estraga o
 * relatorio do mes sem deixar rastro.
 */
export function RulesPage() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const rules = useQuery({ queryKey: ['rules'], queryFn: () => api.rules() });
  const categories = useQuery({ queryKey: ['categories'], queryFn: () => api.categories() });

  const flatCategories = useMemo(
    () => flatten(categories.data?.categories ?? []),
    [categories.data],
  );

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['rules'] });
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const toggle = useMutation({
    mutationFn: (rule: Rule) => api.updateRule(rule.id, { enabled: !rule.enabled }),
    onSuccess: refresh,
    onError: (caught) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteRule(id),
    onSuccess: refresh,
    onError: (caught) => setError(messageFor(caught)),
  });

  const apply = useMutation({
    mutationFn: (dryRun: boolean) => api.applyRules({ dryRun }),
    onSuccess: (result) => {
      setError(null);
      const resumo = result.dryRun
        ? `${result.matched} lancamento(s) receberiam categoria. Nada foi gravado.`
        : `${result.updated} lancamento(s) categorizado(s).`;

      setNotice(
        result.limitReached
          ? `${resumo} A varredura parou no teto por requisicao — rode de novo para continuar.`
          : resumo,
      );
      refresh();
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  return (
    <div className="main">
      <header className="page-header">
        <div className="between">
          <div>
            <h1>Regras</h1>
            <p>
              Quem decide a categoria de &quot;COMPRA CARTAO POSTO IPIRANGA 0412&quot; sem voce
              precisar clicar toda vez.
            </p>
          </div>
          <button className="btn" onClick={() => setCreating(!creating)}>
            {creating ? 'Cancelar' : 'Nova regra'}
          </button>
        </div>
      </header>

      {error && (
        <div className="alert error" role="alert">
          {error}
        </div>
      )}

      {notice && (
        <div className="alert success" role="status">
          {notice}
        </div>
      )}

      {creating && (
        <RuleForm
          categories={flatCategories}
          onDone={() => {
            setCreating(false);
            setError(null);
            refresh();
          }}
          onError={setError}
        />
      )}

      <section className="card">
        <div className="between" style={{ marginBottom: 'var(--space-4)' }}>
          <div>
            <h2 className="card-title">Aplicar ao que ja esta lancado</h2>
            <p className="card-hint" style={{ marginBottom: 0 }}>
              So mexe no que esta sem categoria. O que voce escolheu na mao fica como esta.
            </p>
          </div>
          <div className="row">
            <button
              className="btn ghost"
              onClick={() => apply.mutate(true)}
              disabled={apply.isPending}
            >
              Simular
            </button>
            <button
              className="btn secondary"
              onClick={() => apply.mutate(false)}
              disabled={apply.isPending}
            >
              Aplicar
            </button>
          </div>
        </div>
      </section>

      <section className="card">
        <h2 className="card-title">Na ordem em que sao avaliadas</h2>
        <p className="card-hint">
          A primeira regra que casa por inteiro vence; as outras nem rodam.
        </p>

        {(rules.data?.rules ?? []).length === 0 ? (
          <p className="card-hint" style={{ marginBottom: 0 }}>
            Nenhuma regra ainda.
          </p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'right' }}>Ordem</th>
                  <th>Regra</th>
                  <th>Quando</th>
                  <th>Categoria</th>
                  <th style={{ textAlign: 'right' }}>Usos</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(rules.data?.rules ?? []).map((rule) => (
                  <tr key={rule.id} style={{ opacity: rule.enabled ? 1 : 0.5 }}>
                    <td className="tabular" style={{ textAlign: 'right' }}>
                      {rule.priority}
                    </td>
                    <td>
                      {rule.name}
                      {/* Regra que aparece do nada e nao se distingue das
                          suas e regra em que ninguem confia. */}
                      {rule.learned && (
                        <div
                          className="origem"
                          title="Nasceu de uma categoria que voce escolheu num lancamento."
                        >
                          aprendida com voce
                        </div>
                      )}
                    </td>
                    <td className="muted">
                      {rule.conditions.map(describeCondition).join(' e ')}
                      {rule.kind && (
                        <div style={{ fontSize: 'var(--text-xs)' }}>
                          so em {rule.kind === 'INCOME' ? 'entradas' : 'saidas'}
                        </div>
                      )}
                    </td>
                    <td>{rule.categoryName ?? '—'}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>
                      {rule.appliedCount}
                    </td>
                    <td>
                      <div className="row">
                        <button className="btn ghost" onClick={() => toggle.mutate(rule)}>
                          {rule.enabled ? 'Desligar' : 'Ligar'}
                        </button>
                        <button className="btn danger" onClick={() => remove.mutate(rule.id)}>
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const FIELD_LABELS: Record<RuleField, string> = {
  DESCRIPTION: 'a descricao',
  COUNTERPARTY: 'quem recebeu',
  AMOUNT: 'o valor',
  ACCOUNT: 'a conta',
};

const OPERATOR_LABELS: Record<RuleOperator, string> = {
  CONTAINS: 'contem',
  EQUALS: 'e igual a',
  STARTS_WITH: 'comeca com',
  ENDS_WITH: 'termina com',
  REGEX: 'casa com a expressao',
  GREATER_THAN: 'e maior que',
  LESS_THAN: 'e menor que',
  BETWEEN: 'esta entre',
};

function describeCondition(condition: Rule['conditions'][number]): string {
  const start = `${FIELD_LABELS[condition.field]} ${OPERATOR_LABELS[condition.operator]}`;

  if (condition.field === 'AMOUNT') {
    if (condition.operator === 'BETWEEN') {
      return `${start} ${money(condition.amountMinCents ?? '0')} e ${money(condition.amountMaxCents ?? '0')}`;
    }
    return `${start} ${money(condition.amountMaxCents ?? condition.amountMinCents ?? '0')}`;
  }

  return `${start} "${condition.value ?? ''}"`;
}

interface FlatCategory {
  id: string;
  path: string;
  kind: 'INCOME' | 'EXPENSE';
}

function RuleForm({
  categories,
  onDone,
  onError,
}: {
  categories: FlatCategory[];
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [priority, setPriority] = useState('100');
  const [kind, setKind] = useState<'' | 'INCOME' | 'EXPENSE'>('EXPENSE');
  const [categoryId, setCategoryId] = useState('');
  const [field, setField] = useState<RuleField>('DESCRIPTION');
  const [operator, setOperator] = useState<RuleOperator>('CONTAINS');
  const [value, setValue] = useState('');
  const [amount, setAmount] = useState('');

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.createRule(payload),
    onSuccess: onDone,
    onError: (caught) => onError(messageFor(caught)),
  });

  const isAmount = field === 'AMOUNT';

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();

    try {
      const condition = isAmount
        ? {
            field,
            operator,
            ...(operator === 'LESS_THAN'
              ? { amountMaxCents: parseAmountInput(amount).toString() }
              : { amountMinCents: parseAmountInput(amount).toString() }),
          }
        : { field, operator, value };

      create.mutate({
        name,
        priority: Number(priority),
        kind: kind === '' ? null : kind,
        categoryId: categoryId || null,
        conditions: [condition],
      });
    } catch {
      onError('Valor invalido. Use o formato 1.234,56.');
    }
  };

  return (
    <section className="card">
      <h2 className="card-title">Nova regra</h2>

      <form onSubmit={submit}>
        <div className="row">
          <div className="field" style={{ flex: 2, minWidth: 200 }}>
            <label htmlFor="rule-name">Nome</label>
            <input
              id="rule-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Posto de gasolina"
              required
            />
          </div>

          <div className="field" style={{ width: 110 }}>
            <label htmlFor="rule-priority">Ordem</label>
            <input
              id="rule-priority"
              type="number"
              min={0}
              max={9999}
              value={priority}
              onChange={(event) => setPriority(event.target.value)}
            />
          </div>

          <div className="field" style={{ width: 150 }}>
            <label htmlFor="rule-kind">Vale para</label>
            <select
              id="rule-kind"
              value={kind}
              onChange={(event) => setKind(event.target.value as typeof kind)}
            >
              <option value="EXPENSE">Saidas</option>
              <option value="INCOME">Entradas</option>
              <option value="">As duas</option>
            </select>
          </div>
        </div>

        <div className="row">
          <div className="field" style={{ width: 160 }}>
            <label htmlFor="rule-field">Quando</label>
            <select
              id="rule-field"
              value={field}
              onChange={(event) => {
                const next = event.target.value as RuleField;
                setField(next);
                setOperator(next === 'AMOUNT' ? 'GREATER_THAN' : 'CONTAINS');
              }}
            >
              <option value="DESCRIPTION">A descricao</option>
              <option value="COUNTERPARTY">Quem recebeu</option>
              <option value="ACCOUNT">A conta</option>
              <option value="AMOUNT">O valor</option>
            </select>
          </div>

          <div className="field" style={{ width: 190 }}>
            <label htmlFor="rule-operator">Condicao</label>
            <select
              id="rule-operator"
              value={operator}
              onChange={(event) => setOperator(event.target.value as RuleOperator)}
            >
              {(isAmount
                ? (['GREATER_THAN', 'LESS_THAN', 'EQUALS'] as RuleOperator[])
                : (['CONTAINS', 'STARTS_WITH', 'ENDS_WITH', 'EQUALS', 'REGEX'] as RuleOperator[])
              ).map((option) => (
                <option key={option} value={option}>
                  {OPERATOR_LABELS[option]}
                </option>
              ))}
            </select>
          </div>

          <div className="field" style={{ flex: 1, minWidth: 180 }}>
            <label htmlFor="rule-value">{isAmount ? 'Valor' : 'Texto'}</label>
            <input
              id="rule-value"
              value={isAmount ? amount : value}
              onChange={(event) =>
                isAmount ? setAmount(event.target.value) : setValue(event.target.value)
              }
              placeholder={isAmount ? '500,00' : 'posto ipiranga'}
              required
            />
            {!isAmount && (
              <span className="field-hint">
                Acento e maiuscula nao contam: &quot;sao joao&quot; acha &quot;SÃO JOÃO&quot;.
              </span>
            )}
          </div>
        </div>

        <div className="field">
          <label htmlFor="rule-category">Categoria a aplicar</label>
          <select
            id="rule-category"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            required
          >
            <option value="">Escolha a categoria</option>
            {categories
              .filter((category) => kind === '' || category.kind === kind)
              .map((category) => (
                <option key={category.id} value={category.id}>
                  {category.path}
                </option>
              ))}
          </select>
        </div>

        <button className="btn" type="submit" disabled={create.isPending}>
          {create.isPending ? 'Salvando...' : 'Criar regra'}
        </button>
      </form>
    </section>
  );
}

function flatten(nodes: CategoryNode[], prefix = ''): FlatCategory[] {
  return nodes.flatMap((node) => {
    const path = prefix ? `${prefix} / ${node.name}` : node.name;
    return [{ id: node.id, path, kind: node.kind }, ...flatten(node.children, path)];
  });
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) return caught.message;
  return 'Nao consegui concluir. Tente de novo.';
}
